// [PHASE B / #003]: Shared helpers for SearchableItemSelector. Per Rule 22
//                    (no inline test fabrications), the production component
//                    imports from here and the unit tests import from here —
//                    a regression in either module surfaces in the test that
//                    asserts the contract.
//
// Three helpers:
//   - matchesAllTokens(text, query) — case-insensitive whitespace-tokenized
//     substring AND match. Replaces the prior fuzzyMatch which allowed
//     in-order character match across long descriptions, producing matches
//     like searching "cloak" → "Moon Sickle" / "backpack" / "manacles".
//   - formatCategoryName(category) — "weapons" → "Weapons",
//     "magical_items" → "Magical Items". Used in search-result subtitles,
//     modal headers, and category-list buttons.
//   - formatRecentFavoriteName(itemKey, item) — single fallback chain
//     (`item.name || EquipmentResolver.formatDisplayName(itemKey) ||
//     itemKey.replace(/_/g, ' ')`) so favorites + recent + search-results
//     all render the same display string for the same item.

import { EquipmentResolver } from './equipmentResolver';

/**
 * Strict word-token substring match.
 *
 * `query` is split on whitespace; every token must appear (case-insensitive)
 * as a substring of `text`. Empty query returns true (no filter).
 *
 * @param {string} text  The haystack string (typically `searchText` =
 *                       `${category} ${displayName} ${description}`).
 * @param {string} query The user's search input.
 * @returns {boolean}
 */
export function matchesAllTokens(text, query) {
  if (!query || typeof query !== 'string') return true;
  if (typeof text !== 'string') return false;
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const textLower = text.toLowerCase();
  return tokens.every((token) => textLower.includes(token));
}

/**
 * Format a BASE_ITEMS category key for display.
 *
 *   weapons        → Weapons
 *   magical_items  → Magical Items
 *   spell_components → Spell Components
 *
 * @param {string} category
 * @returns {string}
 */
export function formatCategoryName(category) {
  if (!category || typeof category !== 'string') return '';
  return category
    .replace(/_/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Resolve the display name for a recent / favorite item entry. Single
 * source of truth so favorites + recent + search results all render the
 * same string for the same item.
 *
 * Fallback chain:
 *   1. item.name (BASE_ITEMS row sets this for most named items / magic items)
 *   2. EquipmentResolver.formatDisplayName(itemKey) (handles "+1 Longsword"
 *      style names from itemKey conventions)
 *   3. itemKey with underscores replaced by spaces (last-resort)
 *
 * @param {string} itemKey  BASE_ITEMS sub-key (e.g. "longsword").
 * @param {object} [item]   The BASE_ITEMS row, optional.
 * @returns {string}
 */
export function formatRecentFavoriteName(itemKey, item) {
  if (item && item.name) return item.name;
  const fromResolver = EquipmentResolver.formatDisplayName(itemKey);
  if (fromResolver) return fromResolver;
  if (typeof itemKey === 'string') return itemKey.replace(/_/g, ' ');
  return '';
}
